import type { ColorPalette, ChartColors } from './types';

export const CORE_PALETTE: ColorPalette = {
  forestGreen: 'hsl(var(--chart-forest))',
  forestGreenLight: 'hsl(var(--chart-forest) / 0.12)',
  honeydew: '#D3E2E3',
  honeydewDark: '#9BC0C2',
  actionOrange: '#FF6D33',
  actionOrangeLight: '#FF6D331f',
  white: '#FFFFFF',
  teal: '#4ECDC4',
  tealLight: '#4ECDC41f',
  purple: '#A282FF',
  purpleLight: '#A282FF1f',
  green: '#17927D',
  greenLight: '#17927D1f',
  amber: '#F9B42E',
  amberLight: '#F9B42E1f',
  red: '#E02A79',
  redLight: '#E02A791f',
  blue: '#137A9B',
  blueLight: '#137A9B1f',
  emerald: '#17927D',
  emeraldLight: '#17927D1f',
  pink: '#FF94CE',
  pinkLight: '#FF94CE1f',
  indigo: '#6A4BB6',
  indigoLight: '#6A4BB61f',
  cyan: '#3AC2E5',
  cyanLight: '#3AC2E51f',
  lime: '#4ECDC4',
  limeLight: '#4ECDC41f',
  rose: '#E02A79',
  roseLight: '#E02A791f',
  violet: '#6A4BB6',
  violetLight: '#6A4BB61f',
  fuchsia: '#FF94CE',
  fuchsiaLight: '#FF94CE1f',
  sky: '#3AC2E5',
  skyLight: '#3AC2E51f',
  orange: '#E18606',
  orangeLight: '#E186061f',
  slate: '#64748b',
  slateLight: '#64748b1f'
};

export const getChartColors = (): ChartColors => ({
  primary: [
    { border: CORE_PALETTE.actionOrange, fill: CORE_PALETTE.actionOrangeLight },
    { border: CORE_PALETTE.forestGreen,  fill: CORE_PALETTE.forestGreenLight },
    { border: CORE_PALETTE.teal,         fill: CORE_PALETTE.tealLight },
    { border: CORE_PALETTE.purple,       fill: CORE_PALETTE.purpleLight },
    { border: CORE_PALETTE.amber,        fill: CORE_PALETTE.amberLight },
    { border: CORE_PALETTE.cyan,         fill: CORE_PALETTE.cyanLight },
    { border: CORE_PALETTE.pink,         fill: CORE_PALETTE.pinkLight },
    { border: CORE_PALETTE.emerald,      fill: CORE_PALETTE.emeraldLight },
    { border: CORE_PALETTE.indigo,       fill: CORE_PALETTE.indigoLight },
    { border: CORE_PALETTE.orange,       fill: CORE_PALETTE.orangeLight },
    { border: CORE_PALETTE.blue,         fill: CORE_PALETTE.blueLight },
    { border: CORE_PALETTE.rose,         fill: CORE_PALETTE.roseLight },
    { border: CORE_PALETTE.honeydewDark, fill: CORE_PALETTE.honeydew },
    { border: CORE_PALETTE.slate,        fill: CORE_PALETTE.slateLight }
  ],
  solid: [
    CORE_PALETTE.actionOrange, // #FF6D33
    CORE_PALETTE.forestGreen,  // #002129
    CORE_PALETTE.teal,         // #4ECDC4
    CORE_PALETTE.purple,       // #A282FF
    CORE_PALETTE.amber,        // #F9B42E
    CORE_PALETTE.cyan,         // #3AC2E5
    CORE_PALETTE.pink,         // #FF94CE
    CORE_PALETTE.emerald,      // #17927D
    CORE_PALETTE.indigo,       // #6A4BB6
    CORE_PALETTE.orange,       // #E18606
    CORE_PALETTE.blue,         // #137A9B
    CORE_PALETTE.rose,         // #E02A79
    CORE_PALETTE.honeydewDark,
    CORE_PALETTE.slate
  ]
});

export const EMPLOYEE_GRADE_ORDER: string[] = [
  'Trainee',
  'Semi Senior',
  'Senior',
  'Assistant Manager',
  'Manager',
  'Senior Manager',
  'Associate Director',
  'Director'
];

export const AXIS_TICK_STYLE = { fontSize: 11 } as const;

export const CHART_MARGIN = { top: 5, right: 10, bottom: 5, left: 0 } as const;

export const CHART_MARGIN_DUAL_Y = { top: 5, right: 45, bottom: 5, left: 0 } as const;

export const LABEL_DENSITY_THRESHOLDS = {
  EVERY_MONTH:    60,
  EVERY_2_MONTHS: 40,
  EVERY_3_MONTHS: 25,
  EVERY_6_MONTHS: 15,
} as const;

export const CHART_CARD_PADDING = 16;
export const Y_AXIS_WIDTH_ESTIMATE = 55;
export const Y_AXIS_WIDTH_ESTIMATE_DUAL = 110;

export function getXAxisInterval(
  cardWidth: number,
  margin: { left: number; right: number },
  labelCount: number,
  dualYAxis = false,
): number {
  if (cardWidth === 0 || labelCount === 0) return 2; // fallback: every 3rd
  const yW = dualYAxis ? Y_AXIS_WIDTH_ESTIMATE_DUAL : Y_AXIS_WIDTH_ESTIMATE;
  const plotWidth = Math.max(50, cardWidth - CHART_CARD_PADDING - margin.left - margin.right - yW);
  const pxPerMonth = plotWidth / labelCount;

  if (pxPerMonth >= LABEL_DENSITY_THRESHOLDS.EVERY_MONTH)    return 0;
  if (pxPerMonth >= LABEL_DENSITY_THRESHOLDS.EVERY_2_MONTHS) return 1;
  if (pxPerMonth >= LABEL_DENSITY_THRESHOLDS.EVERY_3_MONTHS) return 2;
  if (pxPerMonth >= LABEL_DENSITY_THRESHOLDS.EVERY_6_MONTHS) return 5;
  return 11;
}


export function renderCategoryTick(props: any): JSX.Element {
  const { x, y, payload } = props;
  return (
    <text x={x} y={y} textAnchor="end" dominantBaseline="middle" style={{ fontSize: 11 }}>
      {payload.value}
    </text>
  );
}

export const MONTH_YEAR_AXIS_HEIGHT = 42;

export function renderMonthYearTick(props: any): JSX.Element {
  const { x, y, payload, tickFormatter } = props;
  const raw = payload?.value;
  const value = String(typeof tickFormatter === 'function' ? tickFormatter(raw) : raw ?? '');
  const splitAt = value.lastIndexOf(' ');

  if (splitAt === -1) {
    return (
      <text x={x} y={y} dy="0.71em" textAnchor="middle" style={{ fontSize: 11 }}>
        {value}
      </text>
    );
  }

  return (
    <text x={x} y={y} textAnchor="middle" style={{ fontSize: 11 }}>
      <tspan x={x} dy="0.71em">{value.slice(0, splitAt)}</tspan>
      <tspan x={x} dy="1.15em">{value.slice(splitAt + 1)}</tspan>
    </text>
  );
}

export type YAxisType = 'percent' | 'hours' | 'gbp' | 'rate';

export function formatYAxisLabel(value: number, type: YAxisType): string {
  if (type === 'percent') return `${value}%`;
  if (type === 'rate')    return `£${value}`;
  const prefix = type === 'gbp' ? '£' : '';
  if (value >= 1_000_000) return `${prefix}${(value / 1_000_000).toFixed(1)}m`;
  if (value >= 1_000)     return `${prefix}${Math.round(value / 1_000)}K`;
  return `${prefix}${value}`;
}

export const getAnimationConfig = () => ({
  duration: 700,
  easing: 'easeInOutQuart' as const,
  delay: (context: any) => {
    let delay = 0;
    if (context.type === 'data' && context.mode === 'default') {
      delay = context.dataIndex * 50 + context.datasetIndex * 100;
    }
    return delay;
  }
});
